import { Playout } from './../../../interfaces/playout';
import { Component, ViewChild, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { PublicationService } from '../../../services/publication.service';
import { CustomFieldsService } from '../../../services/custom-fields.service';
import { AbstractDetailsComponent } from '../../../components/shared/abstract-details.component';
import { NgForm } from '@angular/forms';
import { SapiService, SapiQueryResponse, SapiEntity } from '../../../services/sapi.service';
import { BbTableService } from '../../../services/bb-table.service';
import { Location } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { AdBlockDetectorService } from '../../../services/ad-block-detector.service';
import { DateRange } from '../../../interfaces/dateRange';
import { Adunit } from '../adunit/adunit-list.component';
import { AdunitStats } from '../../../shared/models/sapi/adunit-stats.model';
import { AdunitStatsService } from '../../../services/sapi/adunit-stats.service';

import dayjs from 'dayjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Observable, of, forkJoin } from 'rxjs';
import { AdServicesService } from '../../../services/ad-services.service';

export interface LineItem extends SapiEntity {
	code?: string;
	title?: string;
	timeout: number;
	relatedAdunits?: string[];
	adunits?: Adunit[];
	status: string;
	playout: Playout;
	isVMAP?: boolean;
	type?: string;
	vast_url?: string;
	auctionId?: string;
	subtype?: 'auction';
	usePrebid?: string;
}

interface AdunitItem extends Adunit {
	hidden?: boolean;
}

@Component({
	selector: 'lineitem-details',
	templateUrl: './lineitem-details.component.html',
	styleUrls: ['./lineitem-details.component.scss']
})

export class LineitemDetailsComponent extends AbstractDetailsComponent {

	@ViewChild('formRef', { static: true }) form: NgForm;

	// TODO: Implement minDate
	minDate: Date;

	activeTab: string;
	adBlocker: boolean;

	statsDate: DateRange;

	lineitem: LineItem;
	lineitemDefault: any;

	relatedAdUnits: Adunit[];
	adunitLineitems: LineItem[][];

	adunitStats: AdunitStats[][];
	statsObject: any;

	constructor(protected route: ActivatedRoute,
		protected location: Location,
		protected publicationService: PublicationService,
		protected sapiService: SapiService,
		protected customFieldsService: CustomFieldsService,
		protected bbTableService: BbTableService,
		protected changeDetectorRef: ChangeDetectorRef,
		protected adBlockDetectorService: AdBlockDetectorService,
		protected adunitStatsService: AdunitStatsService,
		public adServicesService: AdServicesService,
		protected translate: TranslateService) {

		super(route, location);

		this.adBlockDetectorService.adBlocker.subscribe((adBlocker: boolean) => {
			this.adBlocker = adBlocker;
		});

		this.lineitemDefault = require('../../../resources/defaults/lineitem.json');

		this.adunitLineitems = [];
		this.relatedAdUnits = [];
		this.adunitStats = [];

		this.entityConfiguration = {
			name: 'lineitem',
			parentPath: '/adservices/lineitems',
			basePath: '/adservices/lineitems/',
			entityTranslation: 'lineitem',
			noReloadAfterSave: true,
			defaults: this.lineitemDefault
		};

		this.entityLoaded.subscribe(() => {
			this.lineitem = this.entity as LineItem;

			if (!this.isNew) {
				this.getRelatedAdunits();
			}
		});

	}

	lineitemDragged(adunitId: string, event: CdkDragDrop<string[]>) {
		if (event.currentIndex === event.previousIndex) {
			return;
		}
		if (this.relatedAdUnits && this.relatedAdUnits.length) {
			this.relatedAdUnits.map((adunit: Adunit) => {
				if (adunit.id === adunitId) {
					let lineitemArray: any[] = adunit.assignedLineitems;
					const lineitemId = lineitemArray[event.previousIndex];
					lineitemArray[event.previousIndex] = lineitemArray[event.currentIndex];
					lineitemArray[event.currentIndex] = lineitemId;
					adunit.assignedLineitems = lineitemArray;

					lineitemArray = adunit.lineitems;
					const lineitem = lineitemArray[event.previousIndex];
					lineitemArray[event.previousIndex] = lineitemArray[event.currentIndex];
					lineitemArray[event.currentIndex] = lineitem;
					adunit.lineitems = lineitemArray;
				}
			});
		}
		this.formGroup.markAsDirty();
	}

	getCleanAdunit(adunit: any) {
		const adunitCopy: any = { ...adunit };
		delete adunitCopy.outstream;
		delete adunitCopy.hidden;
		delete adunitCopy.saveAction;
		return adunitCopy;
	}

	getAdunitStats(adunit: any) {
		const adunitParams: any = { ...this.statsObject };
		adunitParams.adunit = adunit.code;

		this.adunitStatsService.get('', adunitParams).subscribe((adunitStats: AdunitStats) => {
			this.adunitStats[adunit.id] = adunitStats as any;
		});
	}

	getRelatedAdunits(): Observable<any> {
		if (this.lineitem && this.lineitem.relatedAdunits && this.lineitem.relatedAdunits.length) {
			// let params = {
			// 	'limit': 100,
			// 	'q': 'id:' + this.lineitem.relatedAdunits.join(' OR id:')
			// };

			const adunitCalls: Observable<SapiEntity>[] = [];
			this.lineitem.relatedAdunits.forEach((adunitId) => {
				adunitCalls.push(this.sapiService.get('adunit', adunitId));
			});

			const sapiServiceSubject = forkJoin(adunitCalls);

			sapiServiceSubject.subscribe((adunits: SapiEntity[]) => {
				const relatedAdUnits: Adunit[] = [];
				if (adunits && adunits.length) {
					adunits.forEach((entity: SapiEntity) => {
						relatedAdUnits.push(entity as Adunit);
					});
					this.relatedAdUnits = relatedAdUnits;

					this.relatedAdUnits.forEach((adunit: any, index: number) => {
						if (!adunit.assignedLineitems) {
							adunit.assignedLineitems = [];
						}
						if (adunit.assignedLineitems.indexOf(this.lineitem.id) < 0) {
							adunit.assignedLineitems.push(this.lineitem.id);
							adunit.lineitems.push({ ...this.lineitem });
						}
						this.getAdunitStats(adunit);
						if (adunit.positionType === 'inarticle') {
							adunit.outstream = true;
						}
					});
					this.getAdunitLineItems();
				}
			});
			return sapiServiceSubject;
		}
		return of(false);
	}

	getLineItem(lineitemId: string) {
		// if (Object.keys(this.adunitLineitems).indexOf(lineitemId) > -1) {
		// 	lineitem = this.adunitLineitems[lineitemId];
		// }
		return this.adunitLineitems[lineitemId];
	}

	getAdunitLineItems() {
		let assignedLineItems: string[] = [];

		if (this.relatedAdUnits && this.relatedAdUnits.length) {
			this.relatedAdUnits.forEach((relatedAdunit: Adunit) => {
				if (relatedAdunit && relatedAdunit.assignedLineitems && relatedAdunit.assignedLineitems.length) {
					assignedLineItems = assignedLineItems.concat(relatedAdunit.assignedLineitems);
				}
			});
		}

		assignedLineItems = assignedLineItems.filter((lineitemId: string) => lineitemId !== this.lineitem.id);

		const params = {
			limit: 100,
			q: 'id:' + assignedLineItems.join(' OR id:')
		};

		this.adunitLineitems[this.lineitem.id] = this.lineitem;

		this.sapiService.getUrl('/lineitem', params).subscribe((sapiQueryResponse: SapiQueryResponse) => {
			if (sapiQueryResponse && sapiQueryResponse.items && sapiQueryResponse.items.length) {
				sapiQueryResponse.items.forEach((lineitem: LineItem) => {
					this.adunitLineitems[lineitem.id] = lineitem;
				});
			}
		});
	}

	removeLineitemFromAdunit(adunitId: string, lineitemId: string) {
		this.relatedAdUnits.forEach((adunit: any) => {
			if (adunitId === adunit.id) {
				adunit.assignedLineitems = adunit.assignedLineitems.filter((itemId: string) => lineitemId !== itemId);
				adunit.hidden = true;
			}
			return adunit;
		});
		this.lineitem.relatedAdunits = this.lineitem.relatedAdunits.filter((adId: string) => adunitId !== adId);
		this.formGroup.markAsDirty();
	}

	datesChanged(dateRange: DateRange) {
		this.statsDate = dateRange;

		const statsParams: any = {
			start: this.statsDate.startDate.toISOString(),
			end: this.statsDate.endDate.toISOString(),
		};

		// when requesting stats < 1 day, compare with previous day same time period (other cases handled by backend)
		if (Math.abs(dayjs(statsParams.start).diff(statsParams.end, 'hours')) <= 24) {
			statsParams.compare_start = dayjs(statsParams.start).subtract(1, 'days').toISOString();
			statsParams.compare_end = dayjs(statsParams.end).subtract(1, 'days').toISOString();
		}

		this.statsObject = statsParams;

		if (this.relatedAdUnits) {
			this.relatedAdUnits.forEach((adunit: Adunit) => {
				this.getAdunitStats(adunit);
			});
		}

		this.changeDetectorRef.detectChanges();
	}

	adunitSelected(adunitId: string) {
		if (adunitId) {
			if (!this.lineitem.relatedAdunits) {
				this.lineitem.relatedAdunits = [];
			}
			if (this.lineitem.relatedAdunits.indexOf(adunitId) < 0) {
				this.lineitem.relatedAdunits.push(adunitId);
				this.getRelatedAdunits();
			}
		}
		this.formGroup.markAsDirty();
	}

	save(force?: boolean, extraHeaders?: any, saveDirectly: boolean = false, publish?: boolean): Observable<any> {
		const lineitem: LineItem = this.entity as LineItem;
		lineitem.adunits = this.relatedAdUnits.filter((adunit: AdunitItem) => !adunit.hidden);
		const saveSubject = super.save(force, extraHeaders, saveDirectly, publish);

		saveSubject.subscribe(() => {
			this.formGroup.markAsPristine();
			if (this.isNew) {
				this.router.navigate([this.entityConfiguration.basePath, this.entity.id, 'settings']);
			}
		});

		return saveSubject;
	}
}
